import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchCustomers, createCustomerTransfer, adjustCustomerAccount, getKnownCurrencies } from '../services/api';
import { showSuccess, showError } from './ToastNotifications';
import { clampDecimals, maxInputDecimals, formatAmount, sanitizeAmountInput, amountInputGuard, isPositiveAmount, AMOUNT_MUST_BE_POSITIVE } from '../utils/helpers';
import ImageUploadBox from './ImageUploadBox';
import './CreateTransactionModal.css';

// Two flavours of "no bank/wallet involved" ledger entry, picked via mode tabs:
//
//   1. Customer-to-customer transfer ("飞账") — pure book entry that shifts
//      credit from one customer to another. Same currency on both sides.
//      Calls POST /api/customer-transfers.
//
//   2. Single customer adjustment — touches ONLY one customer's ledger,
//      either decreasing or increasing their balance. No counterparty.
//      Calls POST /api/customers/:id/account-adjustment. Use this when you
//      need to record a one-sided event (a fee charged, a debt forgiven,
//      a settlement received in cash) and intend to classify it as an
//      Expense or Other Profit later from the customer ledger view.
//
// Customer inputs are typeahead searches that hit /api/customers/search. A
// full dropdown listing every customer becomes unusable at scale (tens of
// thousands of rows), so we intentionally don't preload the whole list.

// Default currency list used while the dynamic /api/currencies/known
// fetch is in flight (or if it fails). Keeps the modal usable on slow
// networks. Once the API responds we replace this with whatever the
// system actually has — including foreign fiats (USD/SGD/EUR/…) the
// operator may have introduced via Expenses or initial-balance flows.
const DEFAULT_CURRENCY_OPTIONS = [
  { value: 'MYR',  label: 'MYR (Ringgit)' },
  { value: 'USDT', label: 'USDT' },
  { value: 'USDC', label: 'USDC' },
  { value: 'FDUSD', label: 'FDUSD' },
  { value: 'BTC',  label: 'BTC' },
  { value: 'ETH',  label: 'ETH' }
];

// Friendly labels for known currency codes — only MYR really needs the
// extra context; everything else displays its code as-is. Anything not
// in this map renders with just its uppercase code as label.
const CURRENCY_LABEL_OVERRIDES = { MYR: 'MYR (Ringgit)' };
function buildCurrencyOptions(codes) {
  const arr = (codes || []).map(c => ({
    value: c,
    label: CURRENCY_LABEL_OVERRIDES[c] || c,
  }));
  return arr.length ? arr : DEFAULT_CURRENCY_OPTIONS;
}

// ============================================================================
// CustomerPicker - typeahead search input for customer selection.
// Used twice in this modal (From / To). When no customer is selected it shows
// a search box; debounces requests by 250ms; renders up to 20 ranked results;
// clicking a result collapses the picker into a compact "chip" showing the
// chosen customer, with an × to re-open the search.
// ============================================================================
function formatCustomerLine(c) {
  if (!c) return '';
  const base = c.customer_nickname
    ? `${c.customer_name} (${c.customer_nickname})`
    : c.customer_name;
  return `${base} · ${c.customer_number}`;
}

function CustomerPicker({
  value, // { id, customer_name, customer_number, customer_nickname } | null
  onChange,
  excludeId = null,
  placeholder = 'Search by name, nickname, or phone…',
  disabled = false,
  error = null,
  autoFocus = false
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const lastQueryRef = useRef(0);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  const runSearch = useCallback(async (q) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    // Guard against stale responses by stamping each request.
    const stamp = ++lastQueryRef.current;
    setLoading(true);
    try {
      const data = await searchCustomers(trimmed);
      if (stamp !== lastQueryRef.current) return; // stale
      setResults(Array.isArray(data) ? data : []);
      setHighlight(-1);
    } catch (err) {
      console.error('Customer search failed:', err);
      if (stamp === lastQueryRef.current) setResults([]);
    } finally {
      if (stamp === lastQueryRef.current) setLoading(false);
    }
  }, []);

  const onQueryChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q), 250);
  };

  const pick = (c) => {
    onChange(c);
    setQuery('');
    setResults([]);
    setOpen(false);
    setHighlight(-1);
  };

  const clear = () => {
    onChange(null);
    setQuery('');
    setResults([]);
    setOpen(true);
    setHighlight(-1);
    // Refocus so the operator can start typing again immediately
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, filteredResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && filteredResults[highlight]) {
        e.preventDefault();
        pick(filteredResults[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const filteredResults = excludeId
    ? results.filter(c => String(c.id) !== String(excludeId))
    : results;

  // Selected-chip mode
  if (value) {
    return (
      <div className={`customer-picker customer-picker--selected ${error ? 'has-error' : ''}`}>
        <div className="customer-chip">
          <div className="customer-chip-text">
            <span className="customer-chip-name">{value.customer_name}</span>
            {value.customer_nickname && <span className="customer-chip-nick">({value.customer_nickname})</span>}
            <span className="customer-chip-num">{value.customer_number}</span>
          </div>
          <button
            type="button"
            className="customer-chip-clear"
            onClick={clear}
            disabled={disabled}
            title="Change customer"
            aria-label="Clear selection"
          >
            ×
          </button>
        </div>
        {error && <span className="error-message">{error}</span>}
      </div>
    );
  }

  // Search mode
  return (
    <div className={`customer-picker ${error ? 'has-error' : ''}`}>
      <input
        ref={inputRef}
        type="search"
        className={`form-control ${error ? 'error' : ''}`}
        value={query}
        placeholder={placeholder}
        onChange={onQueryChange}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Delay so a click on a result can fire first
          setTimeout(() => setOpen(false), 150);
        }}
        onKeyDown={onKeyDown}
        disabled={disabled}
        autoComplete="off"
      />
      {open && query.trim().length >= 2 && (
        <div className="customer-results">
          {loading ? (
            <div className="customer-results-msg">Searching…</div>
          ) : filteredResults.length === 0 ? (
            <div className="customer-results-msg">
              No customers match &quot;{query.trim()}&quot;.
            </div>
          ) : (
            filteredResults.map((c, i) => (
              <button
                key={c.id}
                type="button"
                className={`customer-result${i === highlight ? ' customer-result--active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); pick(c); }}
                onMouseEnter={() => setHighlight(i)}
              >
                <span className="customer-result-name">
                  {c.customer_name}
                  {c.customer_nickname && <span className="customer-result-nick"> ({c.customer_nickname})</span>}
                </span>
                <span className="customer-result-num">{c.customer_number}</span>
              </button>
            ))
          )}
        </div>
      )}
      {open && query.trim().length > 0 && query.trim().length < 2 && (
        <div className="customer-results">
          <div className="customer-results-msg">Type at least 2 characters to search.</div>
        </div>
      )}
      {error && <span className="error-message">{error}</span>}
    </div>
  );
}

// ============================================================================
// Main modal
// ============================================================================
function CreateTransactionModal({ isOpen, onClose, onSuccess }) {
  // 'transfer' = customer-to-customer (legacy, default). 'adjustment' = single
  // customer drain/gain (no counterparty). Defaults to transfer to preserve
  // muscle memory for existing operators.
  const [mode, setMode] = useState('transfer');
  const [fromCustomer, setFromCustomer] = useState(null);
  const [toCustomer, setToCustomer] = useState(null);
  // 'decrease' = customer balance goes DOWN (e.g. they paid us, we forgave
  // some of their debt). Backend transaction_type='credit' under the post-flip
  // convention because recordAccountTransaction inverts the caller's intent.
  // 'increase' = customer balance goes UP (e.g. we charged them a fee they
  // now owe). Backend transaction_type='debit'.
  const [direction, setDirection] = useState('decrease');
  const [formData, setFormData] = useState({
    currency_type: 'MYR',
    amount: '',
    description: '',
    transferred_at: ''
  });
  // Multi-line "batch" mode for transfer (飞账): operator entered one transfer
  // that's actually N separate sub-amounts they want to remember individually
  // (e.g. RM70k + RM30k + RM100k = RM200k). All sub-rows share the same
  // sender / recipient / currency / date — only amount and per-line note vary.
  // When `lines` has 2+ entries we send `breakdown` to the backend and the
  // top-level `formData.amount` is derived as the sum (the standalone Amount
  // input is hidden in this mode). When `lines.length === 1` the modal behaves
  // exactly like before (single amount field, no breakdown payload).
  // Adjustment mode does NOT use this; only transfer mode.
  const [lines, setLines] = useState([{ amount: '', description: '' }]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Compulsory receipt proof for this ledger entry (applies to BOTH modes —
  // it's the same "Create Transaction" module). Uploaded via the shared
  // /upload/payment-proof endpoint, which returns a stored /uploads URL.
  const [receiptUrl, setReceiptUrl] = useState('');
  // Dynamic currency list — populated from /api/currencies/known on mount.
  // Falls back to DEFAULT_CURRENCY_OPTIONS until the fetch resolves so the
  // modal is usable instantly even on slow networks.
  const [currencyOptions, setCurrencyOptions] = useState(DEFAULT_CURRENCY_OPTIONS);
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    getKnownCurrencies()
      .then(d => { if (!cancelled) setCurrencyOptions(buildCurrencyOptions(d?.all)); })
      .catch(err => { console.error('Failed to load known currencies:', err); /* keep default */ });
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setMode('transfer');
      setFromCustomer(null);
      setToCustomer(null);
      setDirection('decrease');
      setFormData({ currency_type: 'MYR', amount: '', description: '', transferred_at: '' });
      setLines([{ amount: '', description: '' }]);
      setReceiptUrl('');
      setErrors({});
    }
  }, [isOpen]);

  // Switching modes wipes the side-specific state so the user can't end up
  // with a half-filled form that mixes both flows. Adjustment mode doesn't
  // support multi-line batch entry, so we collapse `lines` back to a single
  // empty row when leaving transfer.
  const switchMode = (next) => {
    if (next === mode) return;
    setMode(next);
    setToCustomer(null);
    setLines([{ amount: '', description: '' }]);
    // Clear the receipt too — a proof uploaded for the other flow must not
    // silently carry over and get attached to the wrong ledger entry.
    setReceiptUrl('');
    setErrors({});
  };

  // Sum of all sub-line amounts (numeric, ignoring blanks). Used as the
  // top-level transfer amount when in multi-line mode AND for the running
  // total readout in the UI.
  const linesTotal = lines.reduce((acc, ln) => {
    const n = parseFloat(ln.amount);
    return acc + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);
  const isMultiLine = mode === 'transfer' && lines.length > 1;
  // The "effective" amount the form is operating on. Single-line mode uses
  // the legacy formData.amount field directly; multi-line mode uses the sum
  // of `lines`. This single getter keeps validation, summary, and submit in
  // sync without duplicating the branch.
  const effectiveAmount = isMultiLine ? linesTotal : parseFloat(formData.amount || 0);

  const updateLine = (idx, patch) => {
    setLines(prev => prev.map((ln, i) => i === idx ? { ...ln, ...patch } : ln));
  };
  const addLine = () => {
    // First time the operator clicks "+ Add another", carry the value already
    // typed into the legacy single Amount input down into lines[0] so it's
    // not silently dropped when we flip to multi-line mode.
    setLines(prev => {
      if (prev.length === 1 && !prev[0].amount && formData.amount) {
        return [{ amount: formData.amount, description: '' }, { amount: '', description: '' }];
      }
      return [...prev, { amount: '', description: '' }];
    });
  };
  const removeLine = (idx) => {
    setLines(prev => {
      const next = prev.filter((_, i) => i !== idx);
      // If we just collapsed back to a single row, push that row's amount
      // into the legacy formData.amount so the standard single-line UI shows
      // the right value (the sub-rows block is hidden when length===1).
      if (next.length === 1) {
        setFormData(fd => ({ ...fd, amount: next[0].amount || '' }));
      }
      return next.length === 0 ? [{ amount: '', description: '' }] : next;
    });
  };

  const validate = () => {
    const next = {};
    if (!fromCustomer) {
      next.from_customer_id = mode === 'transfer'
        ? 'Select the sender (debit side)'
        : 'Select the customer';
    }
    if (mode === 'transfer') {
      if (!toCustomer) next.to_customer_id = 'Select the recipient (credit side)';
      if (fromCustomer && toCustomer && String(fromCustomer.id) === String(toCustomer.id)) {
        next.to_customer_id = 'Sender and recipient must be different';
      }
    }
    if (isMultiLine) {
      // Each sub-line must be a positive number; total > 0 is implied.
      const lineErrors = {};
      lines.forEach((ln, i) => {
        if (!isPositiveAmount(ln.amount)) {
          lineErrors[i] = AMOUNT_MUST_BE_POSITIVE;
        }
      });
      if (Object.keys(lineErrors).length) {
        next.lines = lineErrors;
        next.amount = AMOUNT_MUST_BE_POSITIVE;
      } else if (linesTotal <= 0) {
        next.amount = AMOUNT_MUST_BE_POSITIVE;
      }
    } else if (!isPositiveAmount(formData.amount)) {
      next.amount = AMOUNT_MUST_BE_POSITIVE;
    }
    if (!formData.currency_type) {
      next.currency_type = 'Select a currency';
    }
    if (!receiptUrl) {
      next.receipt = 'A receipt image is required';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setErrors({});
    try {
      if (mode === 'transfer') {
        // Build the payload. Multi-line mode → send the SUM as `amount` and
        // the per-row entries as `breakdown` so the backend can stash them
        // as JSON metadata; the actual ledger pair only sees the total.
        // Single-line mode → send the legacy shape unchanged.
        const totalAmount = isMultiLine ? linesTotal : parseFloat(formData.amount);
        const payload = {
          from_customer_id: fromCustomer.id,
          to_customer_id: toCustomer.id,
          amount: totalAmount,
          currency_type: formData.currency_type,
          description: formData.description || undefined,
          transferred_at: formData.transferred_at || undefined,
          receipt_url: receiptUrl
        };
        if (isMultiLine) {
          payload.breakdown = lines.map(ln => ({
            amount: parseFloat(ln.amount),
            description: ln.description || ''
          }));
        }
        const result = await createCustomerTransfer(payload);
        const summaryAmount = formatAmount(totalAmount, formData.currency_type);
        showSuccess(
          isMultiLine
            ? `Recorded: ${result.from.customer_name} → ${result.to.customer_name} ${summaryAmount} (${lines.length} sub-transfers)`
            : `Recorded: ${result.from.customer_name} → ${result.to.customer_name} ${summaryAmount}`
        );
        if (onSuccess) onSuccess(result);
      } else {
        // Single-customer adjustment.
        // Mapping (post-flip convention):
        //   direction='decrease' (balance goes DOWN) → caller passes 'credit',
        //     recordAccountTransaction stores it as 'debit' and SUBTRACTS amount.
        //   direction='increase' (balance goes UP)   → caller passes 'debit',
        //     recordAccountTransaction stores it as 'credit' and ADDS amount.
        const transaction_type = direction === 'decrease' ? 'credit' : 'debit';
        await adjustCustomerAccount(fromCustomer.id, {
          currency_type: formData.currency_type,
          transaction_type,
          amount: parseFloat(formData.amount),
          description: formData.description || (
            direction === 'decrease'
              ? 'Single-customer adjustment (decrease balance)'
              : 'Single-customer adjustment (increase balance)'
          ),
          transferred_at: formData.transferred_at || undefined,
          receipt_url: receiptUrl
        });
        const verb = direction === 'decrease' ? 'Decreased' : 'Increased';
        showSuccess(
          `${verb} ${fromCustomer.customer_name}'s balance by ${formatAmount(formData.amount, formData.currency_type)}. Classify it as Expense / Other Profit from the customer's ledger view when ready.`
        );
        if (onSuccess) onSuccess({ adjustment: true });
      }
      setTimeout(() => onClose(), 600);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to record transaction';
      showError(msg);
      setErrors({ submit: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content create-transaction-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>Create Transaction</h3>
            <p className="modal-subtitle">
              {mode === 'transfer'
                ? 'Direct customer-to-customer ledger transfer. No bank account or wallet is touched — the sender\'s balance decreases and the recipient\'s balance increases by the same amount.'
                : 'Single-customer ledger adjustment. No counterparty — just decrease or increase one customer\'s balance. Classify it as Expense or Other Profit later from the customer\'s ledger view.'}
            </p>
          </div>
          <button type="button" onClick={handleClose} className="modal-close" disabled={submitting}>×</button>
        </div>

        <div className="modal-body">
          {/* Mode tabs — switch between transfer and single-customer adjustment.
              Stays inside the modal (vs separate page) because the field set
              overlaps almost entirely; only the receiver / direction differs. */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #e5e7eb' }}>
            <button
              type="button"
              onClick={() => switchMode('transfer')}
              disabled={submitting}
              style={{
                padding: '8px 14px',
                background: 'none',
                border: 'none',
                borderBottom: mode === 'transfer' ? '2px solid #2563eb' : '2px solid transparent',
                color: mode === 'transfer' ? '#2563eb' : '#6b7280',
                fontWeight: mode === 'transfer' ? 600 : 400,
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              Customer-to-customer transfer (飞账)
            </button>
            <button
              type="button"
              onClick={() => switchMode('adjustment')}
              disabled={submitting}
              style={{
                padding: '8px 14px',
                background: 'none',
                border: 'none',
                borderBottom: mode === 'adjustment' ? '2px solid #2563eb' : '2px solid transparent',
                color: mode === 'adjustment' ? '#2563eb' : '#6b7280',
                fontWeight: mode === 'adjustment' ? 600 : 400,
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              Single customer adjustment (no receiver)
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="transfer-grid">
              <div className="form-group">
                <label className="form-label">
                  {mode === 'transfer' ? (
                    <>
                      From customer <span className="required">*</span>
                      <span className="form-hint">(debit — balance decreases)</span>
                    </>
                  ) : (
                    <>
                      Customer <span className="required">*</span>
                      <span className="form-hint">(the only side touched)</span>
                    </>
                  )}
                </label>
                <CustomerPicker
                  value={fromCustomer}
                  onChange={(c) => {
                    setFromCustomer(c);
                    if (c && toCustomer && String(c.id) === String(toCustomer.id)) {
                      setToCustomer(null);
                    }
                  }}
                  excludeId={toCustomer?.id}
                  placeholder={mode === 'transfer' ? 'Search sender…' : 'Search customer…'}
                  disabled={submitting}
                  error={errors.from_customer_id}
                  autoFocus
                />
              </div>

              {mode === 'transfer' && (
                <>
                  <div className="transfer-arrow" aria-hidden="true">→</div>

                  <div className="form-group">
                    <label className="form-label">
                      To customer <span className="required">*</span>
                      <span className="form-hint">(credit — balance increases)</span>
                    </label>
                    <CustomerPicker
                      value={toCustomer}
                      onChange={setToCustomer}
                      excludeId={fromCustomer?.id}
                      placeholder="Search recipient…"
                      disabled={submitting}
                      error={errors.to_customer_id}
                    />
                  </div>
                </>
              )}
            </div>

            {mode === 'adjustment' && (
              <div className="form-group">
                <label className="form-label">Direction <span className="required">*</span></label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setDirection('decrease')}
                    disabled={submitting}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '6px',
                      border: direction === 'decrease' ? '2px solid #dc2626' : '1px solid #d1d5db',
                      background: direction === 'decrease' ? '#fee2e2' : 'white',
                      color: direction === 'decrease' ? '#991b1b' : '#374151',
                      fontWeight: direction === 'decrease' ? 600 : 400,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <div>− Decrease customer balance</div>
                    <small style={{ color: '#6b7280', fontWeight: 400 }}>
                      e.g. customer paid us cash, or we forgave part of their debt
                      · money out → can be an Expense
                    </small>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDirection('increase')}
                    disabled={submitting}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '6px',
                      border: direction === 'increase' ? '2px solid #16a34a' : '1px solid #d1d5db',
                      background: direction === 'increase' ? '#dcfce7' : 'white',
                      color: direction === 'increase' ? '#14532d' : '#374151',
                      fontWeight: direction === 'increase' ? 600 : 400,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <div>+ Increase customer balance</div>
                    <small style={{ color: '#6b7280', fontWeight: 400 }}>
                      e.g. we charged them a fee they now owe, or recorded a debt they took on
                      · income → can be Other Profit
                    </small>
                  </button>
                </div>
                <small className="form-hint" style={{ marginTop: '6px', display: 'block' }}>
                  Reminder: positive customer balance means the customer owes us.
                  After saving, open the customer's ledger view to classify this
                  entry as <strong>Expense</strong> or <strong>Other Profit</strong>
                  — either direction can be tagged either way, so pick whatever
                  matches the real-world event (e.g. money you spent → Expense).
                </small>
              </div>
            )}

            <div className="inline-row">
              <div className="form-group form-group-currency">
                <label className="form-label">Currency <span className="required">*</span></label>
                <select
                  className={`form-control ${errors.currency_type ? 'error' : ''}`}
                  value={formData.currency_type}
                  onChange={(e) => setFormData({ ...formData, currency_type: e.target.value })}
                  disabled={submitting}
                >
                  {currencyOptions.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              {/* Single-line mode: legacy single Amount input. Hidden as soon
                  as the operator clicks "+ Add another sub-transfer" and we
                  flip into the multi-line list below. */}
              {!isMultiLine && (
                <div className="form-group form-group-amount">
                  <label className="form-label">Amount <span className="required">*</span></label>
                  <input
                    type="number"
                    step={maxInputDecimals(formData.currency_type) === 2 ? '0.01' : '0.00000001'}
                    min="0"
                    {...amountInputGuard}
                    className={`form-control ${errors.amount ? 'error' : ''}`}
                    value={formData.amount}
                    onChange={(e) => {
                      // MYR/fiat/stablecoin → 2 dp; non-stable crypto → 8 dp.
                      const v = clampDecimals(sanitizeAmountInput(e.target.value), maxInputDecimals(formData.currency_type));
                      setFormData({ ...formData, amount: v });
                      // Keep lines[0] in sync so adding another sub-transfer
                      // carries this typed amount as the first sub-row.
                      setLines(prev => prev.length === 1
                        ? [{ ...prev[0], amount: v }]
                        : prev);
                    }}
                    placeholder="0.00"
                    disabled={submitting}
                  />
                  {errors.amount && <span className="error-message">{errors.amount}</span>}
                </div>
              )}
            </div>

            {/* Multi-line "batch" entry — only available in transfer mode.
                Same sender / recipient / currency / date apply to ALL rows
                (the backend enforces this; the UI just hides the duplicate
                inputs). On submit we send the SUM as the transfer amount and
                the per-row amounts/descriptions as a `breakdown` metadata
                array. The customer ledger still sees ONE entry of the total;
                Transaction History expands the breakdown for visibility. */}
            {mode === 'transfer' && (
              <div className="form-group">
                {isMultiLine && (
                  <>
                    <label className="form-label">
                      Sub-transfers <span className="required">*</span>
                      <span className="form-hint">
                        same sender / recipient / currency — total goes into the ledger as one entry
                      </span>
                    </label>
                    <div className="batch-lines">
                      {lines.map((ln, i) => {
                        const lineErr = errors.lines && errors.lines[i];
                        return (
                          <div key={i} className="batch-line-row">
                            <div className="batch-line-index">#{i + 1}</div>
                            <input
                              type="number"
                              step={maxInputDecimals(formData.currency_type) === 2 ? '0.01' : '0.00000001'}
                              min="0"
                              {...amountInputGuard}
                              className={`form-control batch-line-amount ${lineErr ? 'error' : ''}`}
                              value={ln.amount}
                              onChange={(e) => updateLine(i, { amount: clampDecimals(sanitizeAmountInput(e.target.value), maxInputDecimals(formData.currency_type)) })}
                              placeholder="0.00"
                              disabled={submitting}
                              aria-label={`Sub-transfer ${i + 1} amount`}
                            />
                            <input
                              type="text"
                              className="form-control batch-line-desc"
                              value={ln.description}
                              onChange={(e) => updateLine(i, { description: e.target.value })}
                              placeholder="Optional note for this line…"
                              disabled={submitting}
                              aria-label={`Sub-transfer ${i + 1} description`}
                            />
                            <button
                              type="button"
                              className="batch-line-remove"
                              onClick={() => removeLine(i)}
                              disabled={submitting || lines.length <= 1}
                              title={lines.length <= 1 ? 'At least one row required' : 'Remove this sub-transfer'}
                              aria-label="Remove sub-transfer"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <div className="batch-total-row">
                      <span className="batch-total-label">Total</span>
                      <span className="batch-total-amount">
                        {formatAmount(linesTotal, formData.currency_type)}
                      </span>
                    </div>
                    {errors.amount && <span className="error-message">{errors.amount}</span>}
                  </>
                )}
                <button
                  type="button"
                  className="batch-add-btn"
                  onClick={addLine}
                  disabled={submitting}
                >
                  + Add another sub-transfer
                </button>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Date (optional — defaults to now)</label>
              <input
                type="datetime-local"
                className="form-control"
                value={formData.transferred_at}
                onChange={(e) => setFormData({ ...formData, transferred_at: e.target.value })}
                disabled={submitting}
              />
              <small className="form-hint">Use this to backdate an already-happened entry.</small>
            </div>

            <div className="form-group">
              <label className="form-label">Description / note</label>
              <input
                type="text"
                className="form-control"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g. Settled rent on A's behalf, Loan repayment…"
                disabled={submitting}
              />
            </div>

            {/* Compulsory receipt proof for this ledger entry. Applies to both
                modes. Stored on the customer_account_transactions row(s) and
                surfaced in Transaction History and the customer statement. */}
            <div className="form-group">
              <label className="form-label">
                Receipt <span className="required">*</span>
                <span className="form-hint">proof of this transaction</span>
              </label>
              <ImageUploadBox
                endpoint="/upload/payment-proof"
                currentUrl={receiptUrl}
                required
                helperText="Upload, paste, or drag a screenshot of the transaction proof."
                onUploadSuccess={(url) => {
                  setReceiptUrl(url);
                  setErrors((prev) => { const n = { ...prev }; delete n.receipt; return n; });
                }}
              />
              {errors.receipt && <span className="error-message">{errors.receipt}</span>}
            </div>

            {/* Confirmation summary. In multi-line mode the debit/credit
                rows show the SUM (which is what actually hits the ledger);
                the per-line breakdown is visible in the list above. */}
            {mode === 'transfer' && fromCustomer && toCustomer && effectiveAmount > 0 && (
              <div className="transfer-summary">
                <div className="summary-row summary-debit">
                  <span className="summary-label">Debit</span>
                  <span className="summary-customer">{formatCustomerLine(fromCustomer)}</span>
                  <span className="summary-amount">−{formatAmount(effectiveAmount, formData.currency_type)}</span>
                </div>
                <div className="summary-row summary-credit">
                  <span className="summary-label">Credit</span>
                  <span className="summary-customer">{formatCustomerLine(toCustomer)}</span>
                  <span className="summary-amount">+{formatAmount(effectiveAmount, formData.currency_type)}</span>
                </div>
                {isMultiLine && (
                  <div className="summary-batch-note">
                    Recorded as ONE ledger entry for the total. Transaction History will expand to show all {lines.length} sub-transfers.
                  </div>
                )}
              </div>
            )}
            {mode === 'adjustment' && fromCustomer && formData.amount && parseFloat(formData.amount) > 0 && (
              <div className="transfer-summary">
                <div className={`summary-row ${direction === 'decrease' ? 'summary-debit' : 'summary-credit'}`}>
                  <span className="summary-label">{direction === 'decrease' ? 'Decrease' : 'Increase'}</span>
                  <span className="summary-customer">{formatCustomerLine(fromCustomer)}</span>
                  <span className="summary-amount">
                    {direction === 'decrease' ? '−' : '+'}{formatAmount(formData.amount, formData.currency_type)}
                  </span>
                </div>
              </div>
            )}

            {errors.submit && <div className="alert alert-error">{errors.submit}</div>}

            <div className="form-actions">
              <button type="button" onClick={handleClose} className="btn btn-outline" disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting
                  ? 'Recording…'
                  : (mode === 'transfer' ? 'Record transfer' : 'Record adjustment')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateTransactionModal;
